import { cx } from "@shared/components/untitledui/util/cx"
import { useLayoutEffect, useRef, useState, type ReactNode } from "react"
import { Button as ButtonPrimitive } from "react-aria-components"
import { PillLabel } from "../add-button/PillLabel"

/** PillLabel's expanded icon gap (0.25rem). */
const LABEL_GAP_PX = 4

/** The nearest ancestor that clips horizontally, or null when nothing before the viewport does. */
const clippingAncestor = (el: HTMLElement): HTMLElement | null => {
  for (let node = el.parentElement; node; node = node.parentElement) {
    if (getComputedStyle(node).overflowX !== "visible") return node
  }
  return null
}

/**
 * Whether the label has room to grow without being clipped. Measured while collapsed:
 * the label's scrollWidth is its natural width (width: 0 + overflow: hidden + nowrap).
 */
const fitsExpanded = (pill: HTMLElement, label: HTMLElement, expandsLeft: boolean) => {
  const needed = label.scrollWidth + LABEL_GAP_PX
  const clip = clippingAncestor(pill)
  const pillRect = pill.getBoundingClientRect()
  if (!clip) {
    return expandsLeft ? pillRect.left - needed >= 0 : pillRect.right + needed <= window.innerWidth
  }
  // Content box, so a border or scrollbar on the clipper isn't counted as usable room.
  const clipRect = clip.getBoundingClientRect()
  const contentLeft = clipRect.left + clip.clientLeft
  return expandsLeft
    ? pillRect.left - needed >= contentLeft
    : pillRect.right + needed <= contentLeft + clip.clientWidth
}

/**
 * A collapsed icon circle that expands into a labeled pill while hovered, pressed, or
 * its menu is open. The pill is absolutely positioned against the button box's left edge,
 * so the icon stays put under the pointer and the label grows out rightward over the page
 * margin — the icon never moves, and the label never covers the title text.
 *
 * Two of these abutting (`attachedSide`) tile a single stadium — the joined menu+Add pill.
 * The shadow belongs to that group, not to the pill: the group element carries
 * `card-pill-shadow`.
 *
 * Exported for the Google Classroom card's buttons, which reuse the same pill design.
 */
export const CardPillButton = ({
  ariaLabel,
  label,
  icon,
  isMenuOpen,
  side = "right",
  attachedSide,
  dataTest,
}: {
  ariaLabel: string
  label: string
  icon: ReactNode
  isMenuOpen: boolean
  /**
   * Which side of the icon the label expands into. `"left"` is for a button with a sibling to
   * its right (the buttons laid out in a row), so the growing label moves away from it rather
   * than over it. A stacked pair (`attachedSide` top/bottom) always grows right.
   */
  side?: "left" | "right"
  /**
   * The edge that abuts a sibling pill, when this one is half of a joined pair: that end stays
   * square so the two read as one pill. The right-hand member (`"left"`) or the lower member
   * (`"top"`) draws the seam. Omitted for a pill standing alone.
   */
  attachedSide?: "left" | "right" | "top" | "bottom"
  dataTest?: string
}) => {
  const expandsLeft = side === "left"

  const pillRef = useRef<HTMLSpanElement>(null)
  const labelRef = useRef<HTMLSpanElement>(null)
  // Near the edge of a scroll container, the label would open into clipped space, showing a
  // half word. Better to stay a plain icon, so re-measure whenever expansion is about to start.
  const [hasRoom, setHasRoom] = useState(true)
  const measure = () => {
    if (pillRef.current && labelRef.current) {
      setHasRoom(fitsExpanded(pillRef.current, labelRef.current, expandsLeft))
    }
  }
  // The menu can open from the keyboard, with no hover to have measured first.
  useLayoutEffect(() => {
    if (isMenuOpen) measure()
  }, [isMenuOpen])

  return (
    // A fixed circular footprint sized to the collapsed pill (14px icon + equal padding);
    // the visible pill is an absolutely-positioned child anchored to the edge it grows away
    // from, so on hover it expands without shifting the row.
    <ButtonPrimitive
      aria-label={ariaLabel}
      onHoverStart={measure}
      data-test={dataTest}
      className="relative h-[calc(14px_+_0.75rem)] w-[calc(14px_+_0.75rem)] shrink-0 cursor-pointer outline-none"
    >
      {({ isHovered, isPressed }) => {
        const expanded = hasRoom && (isHovered || isPressed || isMenuOpen)
        // Reveal animation (width + margin) is driven by Motion in PillLabel; the className
        // sets only the collapsed state.
        const expandingLabel = (
          <PillLabel
            ref={labelRef}
            expanded={expanded}
            side={side}
            className="w-0 overflow-hidden text-nowrap"
          >
            {label}
          </PillLabel>
        )
        return (
          // Pinned to the edge the label grows AWAY from, so the icon stays put under the
          // pointer. Text kept no taller than the icon so the pill height matches the button's.
          <span
            ref={pillRef}
            className={cx(
              "absolute top-0 z-[1] flex items-center bg-white p-1.5 text-[14px] leading-[14px] font-bold text-slate-700 [&_svg]:size-3.5 [&_svg]:shrink-0",
              expandsLeft ? "right-0" : "left-0",
              // A joined pill rounds only its outer end, and the seam-drawing half paints the
              // divider as an absolutely positioned hairline, so it costs the row no width and
              // the icon stays centered in its box.
              attachedSide === undefined && "rounded-full",
              attachedSide === "right" && "rounded-l-full",
              attachedSide === "left" &&
                "rounded-r-full before:absolute before:inset-y-0 before:left-0 before:w-px before:bg-card-hairline before:content-['']",
              // A stacked half's label grows right past its partner, so the corner that
              // overhangs is capped while expanded (eased, since the width springs) and the
              // seam spans only the collapsed footprint the partner sits against. Explicit
              // half-height radii, not rounded-full: when a side's two radii overflow it the
              // browser scales all four together, so a 9999px cap beside the 13px one would
              // swell to the full height and crush the cap.
              attachedSide === "bottom" && "rounded-t-[calc(7px_+_0.375rem)]",
              attachedSide === "bottom" && expanded && "rounded-br-[calc(7px_+_0.375rem)]",
              attachedSide === "top" &&
                "rounded-b-[calc(7px_+_0.375rem)] before:absolute before:top-0 before:left-0 before:h-px before:w-[calc(14px_+_0.75rem)] before:bg-card-hairline before:content-['']",
              attachedSide === "top" && expanded && "rounded-tr-[calc(7px_+_0.375rem)]",
              (attachedSide === "top" || attachedSide === "bottom") &&
                "transition-[border-radius] duration-150",
            )}
          >
            {expandsLeft && expandingLabel}
            {icon}
            {!expandsLeft && expandingLabel}
          </span>
        )
      }}
    </ButtonPrimitive>
  )
}
